import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PublicIcon from "@mui/icons-material/Public";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SafeImage, { pickImage } from "../Global/SafeTmage";
import { fetchToursCached, isDomesticTour, readCache, writeCache } from "./toursApi";
import { states } from "./stateData";

const prettify = (s = "") =>
  s.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Fully data-driven: destinations are derived from the live tour catalogue.
function DestinationShowcase({ selectedState, searchTerm, onPick, onClear }) {
  const [tours, setTours] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [scope, setScope] = useState("india"); // 'india' | 'international'
  const scrollRef = useRef(null);

  // Last visit's destination list paints instantly while fresh data loads.
  // An empty cached list is ignored (it would hide the loading state forever).
  const cachedGroups = useMemo(() => {
    const c = readCache("destinations");
    return c && ((c.india && c.india.length) || (c.intl && c.intl.length)) ? c : null;
  }, []);
  const loaded = tours !== null || !!cachedGroups;

  useEffect(() => {
    let alive = true;
    setError(false);
    fetchToursCached()
      .then((d) => alive && setTours((Array.isArray(d) ? d : []).filter((t) => t.available === true)))
      .catch(() => {
        if (!alive) return;
        setError(true);
        setTours((prev) => prev); // keep cached groups if we have them
      });
    return () => { alive = false; };
  }, [attempt]);

  const { india, intl } = useMemo(() => {
    if (tours === null) return cachedGroups || { india: [], intl: [] };
    const build = (list, keyOf, labelOf, type) => {
      const map = new Map();
      list.forEach((t) => {
        const k = keyOf(t);
        if (!k) return;
        const g = map.get(k) || { key: k, label: labelOf(t, k), type, count: 0, image: null };
        g.count += 1;
        if (!g.image) g.image = pickImage(t.image_url) || null;
        map.set(k, g);
      });
      return [...map.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    };
    const all = tours || [];
    return {
      india: build(
        all.filter(isDomesticTour),
        (t) => t.stateKey || (t.state ? String(t.state).trim().toLowerCase().replace(/\s+/g, "-") : ""),
        (t, k) => states.find((s) => s.key === k)?.label || prettify(k),
        "state"
      ),
      intl: build(
        all.filter((t) => !isDomesticTour(t)),
        (t) => (t.country || "").trim(),
        (t, k) => k,
        "country"
      ),
    };
  }, [tours, cachedGroups]);

  useEffect(() => {
    if (tours !== null) writeCache("destinations", { india, intl });
  }, [tours, india, intl]);

  // Land on whichever scope actually has data
  useEffect(() => {
    if (loaded && scope === "india" && !india.length && intl.length) setScope("international");
  }, [loaded, india.length, intl.length, scope]);

  // Back to the start when switching India / International
  useEffect(() => { scrollRef.current?.scrollTo({ left: 0 }); }, [scope]);

  const items = scope === "india" ? india : intl;
  const isActive = (d) =>
    d.type === "state"
      ? selectedState === d.key
      : (searchTerm || "").trim().toLowerCase() === d.key.toLowerCase();
  const anyActive = items.some(isActive);

  // Arrow visibility (only show an arrow when there is more to scroll that way)
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const rafRef = useRef(0);
  const updateArrows = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = scrollRef.current;
      if (!el) return;
      setCanLeft(el.scrollLeft > 4);
      setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    });
  }, []);
  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    const ro = typeof ResizeObserver !== "undefined" && el ? new ResizeObserver(updateArrows) : null;
    ro && ro.observe(el);
    window.addEventListener("resize", updateArrows);
    return () => {
      ro && ro.disconnect();
      window.removeEventListener("resize", updateArrows);
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [tours, scope, loaded, updateArrows]);

  const scrollByCards = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };

  // NOTE: no wheel hijacking. Vertical wheel/trackpad scrolling always scrolls the
  // page; horizontal swipe / shift+wheel / drag / arrows scroll the strip natively.

  // Click-and-drag scrolling for mouse users (touch / trackpad use native scrolling).
  const drag = useRef({ down: false, moved: false, startX: 0, startLeft: 0 });
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    drag.current = { down: true, moved: false, startX: e.clientX, startLeft: el.scrollLeft };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.down) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) > 5) d.moved = true;
    if (d.moved) scrollRef.current.scrollLeft = d.startLeft - dx;
  };
  const endDrag = () => { drag.current.down = false; };
  // swallow the click that ends a drag so a card isn't picked by accident
  const onClickCapture = (e) => {
    if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); drag.current.moved = false; }
  };

  if (tours !== null && !india.length && !intl.length && !error) return null;

  const cardBase = "group shrink-0 flex flex-col items-center p-2.5 rounded-2xl w-[96px] transition-colors border-2";
  const cardOn = "bg-blue-50 border-blue-600 text-blue-700";
  const cardOff = "bg-[#FAF7F2] border-[#EFE8DB] hover:border-blue-400 text-slate-700";

  // Blue top accent only appears once data is loaded (neutral while loading)
  const topBorder = loaded ? "border-t-blue-600" : "border-t-[#E8E0D2]";

  return (
    <div
      className={`mb-10 min-w-0 bg-white rounded-3xl border border-[#E8E0D2] border-t-4 ${topBorder} shadow-[0_10px_25px_-8px_rgba(15,41,66,0.18)] p-5 sm:p-6 transition-colors`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-5">
        <div>
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] mb-1 ${loaded ? "text-blue-600" : "text-slate-400"}`}>
            Explore by destination
          </p>
          <h3 className="text-xl sm:text-2xl font-extrabold text-[#0B2A5B]">
            {scope === "india" ? "India's Favourite Holiday Destinations" : "Top International Destinations"}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Popular trips at the best prices</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`inline-flex rounded-full p-1 ${loaded ? "bg-blue-100" : "bg-slate-100"}`}>
            {[["india", "India"], ["international", "International"]].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setScope(k)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${
                  scope === k && loaded
                    ? "bg-blue-600 text-white shadow"
                    : scope === k
                    ? "bg-slate-300 text-white"
                    : "text-slate-500 hover:text-blue-700"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative group/strip">
        {canLeft && (
          <button
            type="button"
            aria-label="Scroll destinations left"
            onClick={() => scrollByCards(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-9 h-9 rounded-full bg-white text-blue-700 border border-blue-200 shadow-lg flex items-center justify-center transition hover:bg-blue-600 hover:text-white md:opacity-0 md:group-hover/strip:opacity-100 focus:opacity-100"
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
        )}
        {canRight && (
          <button
            type="button"
            aria-label="Scroll destinations right"
            onClick={() => scrollByCards(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-9 h-9 rounded-full bg-white text-blue-700 border border-blue-200 shadow-lg flex items-center justify-center transition hover:bg-blue-600 hover:text-white md:opacity-0 md:group-hover/strip:opacity-100 focus:opacity-100"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        )}
      <div
        ref={scrollRef}
        onScroll={updateArrows}
        onDragStart={(e) => e.preventDefault()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        className="flex items-stretch gap-3 overflow-x-auto pb-2 overscroll-x-contain cursor-grab active:cursor-grabbing select-none [&_img]:pointer-events-none [&_img]:select-none"
        style={{ scrollbarWidth: "none", touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" }}
      >
        {/* while loading: reserve the space, show nothing (no coloured placeholders) */}
        {!loaded && !error &&
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} aria-hidden="true" className="shrink-0 w-[96px] h-[112px] rounded-2xl bg-[#F3EEE4] animate-pulse" />
          ))}
        {!loaded && error && (
          <div className="h-[104px] w-full flex items-center justify-center gap-3 text-sm text-slate-500">
            Couldn't load destinations.
            <button
              type="button"
              onClick={() => { setError(false); setAttempt((n) => n + 1); }}
              className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold"
            >
              Retry
            </button>
          </div>
        )}

        {loaded && (
          <button type="button" onClick={onClear} className={`${cardBase} ${!anyActive ? cardOn : cardOff}`}>
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mb-2 shadow-sm ${
                !anyActive ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700 group-hover:bg-blue-200"
              }`}
            >
              <PublicIcon />
            </div>
            <span className="text-xs font-bold whitespace-nowrap">{scope === "india" ? "All India" : "All Countries"}</span>
            <span className="text-[10px] text-slate-400">{items.reduce((n, d) => n + d.count, 0)} tours</span>
          </button>
        )}

        {loaded &&
          items.map((d, i) => {
            const active = isActive(d);
            return (
              <button
                key={`${d.type}-${d.key}`}
                type="button"
                onClick={() => onPick(d)}
                className={`${cardBase} ${active ? cardOn : cardOff}`}
              >
                {/* Neutral beige tile + forced-transparent descendants, so no blue placeholder
                    from SafeImage can show while the thumbnail is loading */}
                <div className="relative w-14 h-14 shrink-0 rounded-full overflow-hidden mb-2 shadow-sm bg-[#EFE8DB] [&>*]:absolute [&>*]:inset-0 [&>*]:w-full [&>*]:h-full [&_*]:!bg-transparent [&_img]:w-full [&_img]:h-full [&_img]:object-cover">
                  <SafeImage src={d.image} width={120} fade loading={i < 6 ? "eager" : "lazy"} decoding="async" type="tour" alt={d.label} compact />
                </div>
                <span className={`text-xs whitespace-nowrap ${active ? "font-bold" : "font-semibold"}`}>{d.label}</span>
                <span className="text-[10px] text-slate-400">{d.count} {d.count === 1 ? "tour" : "tours"}</span>
              </button>
            );
          })}
      </div>
      </div>
    </div>
  );
}

export default memo(DestinationShowcase);